import React from 'react';
import _isEmpty from 'lodash/isEmpty';
import _get from 'lodash/get';
import PropTypes from 'prop-types';
// Material UI
import { makeStyles } from '@material-ui/core/styles';
import MuiFormControl from '@material-ui/core/FormControl';
import MuiFormHelperText from '@material-ui/core/FormHelperText';
import MuiInput from '@material-ui/core/Input';
import MuiInputLabel from '@material-ui/core/InputLabel';
import MuiInputAdornment from '@material-ui/core/InputAdornment';

const useStyles = (inputMaxWidth) => makeStyles(() => {
  let input = {};
  if (inputMaxWidth) {
    input = {
      ...input,
      maxWidth: inputMaxWidth,
    };
  }
  return { input };
});

const TextField = React.forwardRef(({
  type,
  label,
  value,
  required,
  errorText,
  fullWidth,
  margin,
  autoComplete,
  disabled,
  readOnly,
  name,
  maxLength,
  adornments,
  inputMaxWidth,
  onChange,
  onBlur,
  onEnterPress,
}, ref) => {
  const classes = useStyles(inputMaxWidth)();

  // Error flag
  const error = !_isEmpty(errorText);

  // Change handler
  // Pass currentTarget because TextField can be used as an anchor element
  const handleChange = onChange ? (e) => onChange(e.target.value, e.currentTarget) : null;
  // Blur handler
  const handleBlur = onBlur ? (e) => onBlur(e.target.value) : null;
  // Key press handler
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && onEnterPress) {
      onEnterPress();
    }
  };

  // Components props
  const formControlProps = {
    required, error, fullWidth, margin,
  };
  const inputProps = {
    inputRef: ref,
    type,
    value,
    autoComplete,
    disabled,
    readOnly,
    onChange: handleChange,
    onBlur: handleBlur,
    onKeyPress: handleKeyPress,
    inputProps: {
      maxLength,
      name,
    },
  };
  // Adornments
  const startAdornment = _get(adornments, 'start');
  const endAdornment = _get(adornments, 'end');
  return (
    <MuiFormControl {...formControlProps} disabled={disabled}>
      {label && (
        <MuiInputLabel>{label}</MuiInputLabel>
      )}
      <MuiInput
        {...inputProps}
        className={classes.input}
        startAdornment={startAdornment && (
          <MuiInputAdornment position="start">
            {startAdornment}
          </MuiInputAdornment>
        )}
        endAdornment={endAdornment && (
          <MuiInputAdornment position="end">
            {endAdornment}
          </MuiInputAdornment>
        )}
      />
      {error && (
        <MuiFormHelperText error>
          {errorText}
        </MuiFormHelperText>
      )}
    </MuiFormControl>
  );
});

TextField.displayName = 'TextField';

TextField.propTypes = {
  type: PropTypes.string,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  required: PropTypes.bool,
  errorText: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  fullWidth: PropTypes.bool,
  margin: PropTypes.oneOf(['none', 'dense', 'normal']),
  autoComplete: PropTypes.string,
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
  adornments: PropTypes.shape({
    start: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
    end: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  }),
  inputMaxWidth: PropTypes.number,
  maxLength: PropTypes.number,
  name: PropTypes.string,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  onEnterPress: PropTypes.func,
};

TextField.defaultProps = {
  type: 'text',
  label: null,
  value: '',
  required: false,
  errorText: null,
  fullWidth: true,
  margin: 'normal',
  autoComplete: 'off',
  disabled: false,
  readOnly: false,
  adornments: null,
  inputMaxWidth: null,
  onChange: null,
  maxLength: null,
  name: null,
  onBlur: null,
  onEnterPress: null,
};

export default React.memo(TextField);



// WEBPACK FOOTER //
// ./components/core/TextField.js